import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Container, Button } from '@mui/material';
import { useAdmin } from '../../contexts/AdminContext';
import { useAuth } from '../../contexts/AuthContext';

interface AdminGuardProps {
  children: ReactNode;
  requiredPermission?: keyof Omit<
    ReturnType<typeof useAdmin>,
    'userRole' | 'permissions' | 'assignAdminRole' | 'removeAdminRole'
  >;
}

export const AdminGuard: React.FC<AdminGuardProps> = ({ 
  children, 
  requiredPermission 
}) => {
  const { user } = useAuth();
  const admin = useAdmin();

  // Check if user is admin
  if (!admin.isAdmin) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access the admin panel.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current role: {admin.userRole || 'user'}
          </Typography>
          <Button 
            variant="contained" 
            href="/dashboard"
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  // Check specific permission if required
  if (requiredPermission) {
    const hasPermission = admin[requiredPermission];
    
    if (!hasPermission) {
      return (
        <Container maxWidth="sm">
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              textAlign: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
              Insufficient Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your role doesn't have permission for this action.
            </Typography>
            <Button 
              variant="outlined" 
              href="/admin"
              sx={{ mt: 2 }}
            >
              Back to Admin Panel
            </Button>
          </Box>
        </Container>
      );
    }
  }

  return <>{children}</>;
};

export default AdminGuard;
